// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  // Function to update email verification status in Firestore
  const updateEmailVerificationStatus = async (userId, isVerified) => {
    if (!userId) return;
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        emailVerified: isVerified,
        emailVerifiedAt: isVerified ? serverTimestamp() : null,
        ...(isVerified && { emailVerifiedDate: serverTimestamp() })
      });
    } catch (err) {
      console.error("Error updating email verification status:", err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          
          if (userDoc.exists()) {
            const existingData = userDoc.data();
            const currentEmailVerified = currentUser.emailVerified;
            const storedEmailVerified = existingData.emailVerified || false;
            
            // Check if email verification status has changed
            if (currentEmailVerified !== storedEmailVerified) {
              // Update Firestore with the correct verification status
              await updateEmailVerificationStatus(currentUser.uid, currentEmailVerified);
              existingData.emailVerified = currentEmailVerified;
              
              // Add verification timestamp if newly verified
              if (currentEmailVerified && !storedEmailVerified) {
                existingData.emailVerifiedAt = serverTimestamp();
              }
            }
            
            setUserData({ uid: currentUser.uid, ...existingData });
            setEmailVerified(currentEmailVerified);
          } else {
            // Create minimal user data if document doesn't exist
            const newUserData = { 
              uid: currentUser.uid, 
              email: currentUser.email,
              emailVerified: currentUser.emailVerified,
              createdAt: serverTimestamp(),
              profileCompleted: false
            };
            setUserData(newUserData);
            setEmailVerified(currentUser.emailVerified);
            
            // Optionally create the user document if it doesn't exist
            // This can be useful for users who signed up via Google but document wasn't created
            try {
              const userRef = doc(db, "users", currentUser.uid);
              await updateDoc(userRef, newUserData);
            } catch (err) {
              console.error("Error creating user document:", err);
            }
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          setError(err.message);
          // Fallback to basic user data
          setUserData({ uid: currentUser.uid, email: currentUser.email });
          setEmailVerified(currentUser.emailVerified);
        }
      } else {
        setUserData(null);
        setEmailVerified(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Periodically check for email verification status changes (every 5 seconds)
  useEffect(() => {
    if (!user) return;
    
    const checkVerificationStatus = async () => {
      try {
        // Reload user to get latest email verification status
        await user.reload();
        const currentVerified = user.emailVerified;
        
        if (currentVerified !== emailVerified) {
          // Update local state
          setEmailVerified(currentVerified);
          
          // Update Firestore
          await updateEmailVerificationStatus(user.uid, currentVerified);
          
          // Also update userData
          setUserData(prev => ({
            ...prev,
            emailVerified: currentVerified,
            ...(currentVerified && { emailVerifiedAt: serverTimestamp() })
          }));
        }
      } catch (err) {
        console.error("Error checking verification status:", err);
      }
    };
    
    // Check immediately
    checkVerificationStatus();
    
    // Set interval to check every 5 seconds
    const interval = setInterval(checkVerificationStatus, 5000);
    
    return () => clearInterval(interval);
  }, [user, emailVerified]);

  const value = {
    user,
    userData,
    loading,
    error,
    isAuthenticated: !!user,
    isMentor: userData?.role === "mentor",
    isUser: userData?.role === "user",
    isVerifiedByAdmin: userData?.isVerifiedByAdmin || false,
    emailVerified: emailVerified,
    // Helper function to manually refresh verification status
    refreshVerificationStatus: async () => {
      if (!user) return;
      try {
        await user.reload();
        const currentVerified = user.emailVerified;
        if (currentVerified !== emailVerified) {
          setEmailVerified(currentVerified);
          await updateEmailVerificationStatus(user.uid, currentVerified);
          setUserData(prev => ({
            ...prev,
            emailVerified: currentVerified,
            ...(currentVerified && { emailVerifiedAt: serverTimestamp() })
          }));
        }
        return currentVerified;
      } catch (err) {
        console.error("Error refreshing verification status:", err);
        return emailVerified;
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};